import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { areEqual } from 'Utils/equalityChecks';

import { CompanyProjectsDataModal } from 'Containers/Projects/Modals/CompanyProjectsModal';
import { ProjectsBase } from 'Components/Projects';
import { firstCompanyIdSelector } from 'Containers/Projects/selectors';
import { useProjectsEvents } from 'Hooks/useProjectsEvents';
import { useProjectsFunctions } from 'Context/Projects';

interface Props {
  projectCategory: string;
  projectsSelector: any;
  fetchingSelector: any;
  active: boolean;
  listProjectsFunction: (companyId: number, sort?: string, page?: number, searchValue?: string) => any;
  onClickRow?: (e: any) => void;
}

function ProjectsBaseContainer({
  active,
  projectsSelector,
  projectCategory,
  fetchingSelector,
  listProjectsFunction,
  onClickRow,
}: Props) {
  const dispatch = useDispatch();

  const firstCompanyId = useSelector(firstCompanyIdSelector, areEqual);
  const projects: CompanyProjectsDataModal = useSelector(projectsSelector, areEqual);
  const fetching = useSelector(fetchingSelector, areEqual);

  const { searchValue }: any = useProjectsFunctions();

  const [sortBy, setSortBy] = useState('-created_at');
  const [pageCount, setPageCount] = useState(1);
  const [initialPage, setInitialPage] = useState(0);

  // project events hook
  const [refreshProjects, setRefreshProjects] = useProjectsEvents();

  const getProjects = useCallback(
    (initialPage = 0) => {
      dispatch(listProjectsFunction(firstCompanyId, sortBy, initialPage + 1, searchValue));
    },
    [firstCompanyId, sortBy, initialPage, searchValue]
  );

  // sort by: -field === DESC, field === ASC
  // prefix with '-' for DESC
  const onClickSort = useCallback(
    (sort: string) => {
      if (sortBy.includes('-') && sortBy === sort) {
        setSortBy(sort.replace('-', ''));
      } else {
        setSortBy(sort);
      }
    },
    [sortBy]
  );

  // initial API call
  // fetch on sort
  // fetch on page change
  // fetch on search
  useEffect(() => {
    if (active && firstCompanyId && (searchValue.length >= 2 || searchValue.length === 0)) {
      setInitialPage(0);
      getProjects();
    }
  }, [active, firstCompanyId, sortBy, searchValue]);

  // set meta data
  useEffect(() => {
    if (projects?.data?.length > 0) {
      const { meta } = projects;
      const { total, per_page: perPage } = meta;

      setPageCount(total <= perPage ? 1 : Math.ceil(total / perPage));
    }
  }, [projects]);

  const onPageChange = useCallback(({ selected: page }: any) => {
    setInitialPage(page);
    getProjects(page);
  }, []);

  // refresh project list on push events
  useEffect(() => {
    if (refreshProjects && active) {
      getProjects();
      setRefreshProjects(false);
    }
  }, [refreshProjects, active]);

  return (
    <ProjectsBase
      projects={projects}
      projectCategory={projectCategory}
      sortBy={sortBy}
      onClickRow={onClickRow}
      onClickSort={onClickSort}
      fetching={fetching}
      initialPage={initialPage}
      pageCount={pageCount}
      onPageChange={onPageChange}
    />
  );
}

ProjectsBaseContainer.defaultProps = {
  onClickRow: null,
};

const ProjectsBaseContainerMemo = memo(ProjectsBaseContainer, areEqual);

export { ProjectsBaseContainerMemo as ProjectsBaseContainer };
